import React, { useState } from 'react';
import { Star, ShoppingCart, Search, SlidersHorizontal, CheckCircle2, Minus, Plus } from 'lucide-react';
import { products, formatPrice } from '../data/mockData';

const CATEGORIES = [
  { id: 'all', label: 'Semua' },
  { id: 'koper', label: 'Koper' },
  { id: 'pakaian', label: 'Pakaian' },
  { id: 'alas_kaki', label: 'Alas Kaki' },
  { id: 'tas', label: 'Tas' },
  { id: 'perlengkapan', label: 'Perlengkapan' },
  { id: 'perawatan', label: 'Perawatan' },
];

const BADGE_COLORS = {
  'BEST SELLER': 'bg-[#F5A623] text-[#0E0E12]',
  'PREMIUM': 'bg-purple-500 text-white',
  'LARIS': 'bg-[#00C9B1] text-[#0E0E12]',
  'COMFORT': 'bg-blue-500 text-white',
  'AMAN': 'bg-green-500 text-white',
  'WAJIB': 'bg-red-500 text-white',
  'PRAKTIS': 'bg-indigo-500 text-white',
  'SEGAR': 'bg-teal-500 text-white',
};

function StarRating({ rating, size = 12 }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star
          key={i}
          size={size}
          className={i <= Math.round(rating) ? 'text-[#F5A623] fill-[#F5A623]' : 'dark:text-gray-600 text-gray-300'}
        />
      ))}
    </div>
  );
}

function ProductCard({ product, onAddToCart, cartCount }) {
  const [imgError, setImgError] = useState(false);
  const discount = Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100);

  return (
    <div
      data-testid={`product-card-${product.id}`}
      className="card-hover rounded-2xl overflow-hidden dark:bg-[#1A1A24] bg-white dark:border dark:border-[#2D3748] border border-[#E2E8F0] flex flex-col"
    >
      {/* Image */}
      <div className="relative h-44 dark:bg-[#242430] bg-gray-100 overflow-hidden">
        {!imgError ? (
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#1A1A24] to-[#242430] flex items-center justify-center">
            <ShoppingCart size={28} className="text-[#2D3748]" />
          </div>
        )}

        {/* Badge */}
        <span className={`absolute top-2.5 left-2.5 px-2 py-0.5 rounded-full text-[10px] font-black tracking-wider ${BADGE_COLORS[product.badge] || 'bg-gray-600 text-white'}`}>
          {product.badge}
        </span>

        {/* Discount */}
        {discount > 0 && (
          <span className="absolute top-2.5 right-2.5 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500 text-white">
            -{discount}%
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-semibold dark:text-white text-[#1A202C] text-sm leading-snug mb-2 line-clamp-2 flex-1">
          {product.name}
        </h3>

        {/* Rating */}
        <div className="flex items-center gap-2 mb-3">
          <StarRating rating={product.rating} />
          <span className="text-xs font-semibold dark:text-gray-300 text-gray-600">{product.rating}</span>
          <span className="text-xs dark:text-gray-500 text-gray-400">({product.reviews})</span>
          <span className="text-xs dark:text-gray-600 text-gray-300 ml-auto">{product.sold} terjual</span>
        </div>

        {/* Price */}
        <div className="mb-3">
          <p className="text-lg font-black text-[#F5A623]">{formatPrice(product.price)}</p>
          {product.originalPrice > product.price && (
            <p className="text-xs dark:text-gray-500 text-gray-400 line-through">{formatPrice(product.originalPrice)}</p>
          )}
        </div>

        {/* Add to Cart */}
        {cartCount > 0 ? (
          <div className="flex items-center justify-between dark:bg-[#242430] bg-[#F7FAFC] rounded-xl p-1">
            <button
              data-testid={`decrease-qty-${product.id}`}
              onClick={() => onAddToCart(product.id, -1)}
              className="w-8 h-8 flex items-center justify-center text-[#00C9B1] hover:bg-[#00C9B1]/10 rounded-lg transition-colors"
            >
              <Minus size={14} />
            </button>
            <span className="text-sm font-bold dark:text-white text-[#1A202C]">{cartCount}</span>
            <button
              data-testid={`increase-qty-${product.id}`}
              onClick={() => onAddToCart(product.id, 1)}
              className="w-8 h-8 flex items-center justify-center text-[#00C9B1] hover:bg-[#00C9B1]/10 rounded-lg transition-colors"
            >
              <Plus size={14} />
            </button>
          </div>
        ) : (
          <button
            data-testid={`add-to-cart-${product.id}`}
            onClick={() => onAddToCart(product.id, 1)}
            className="w-full py-2.5 bg-[#00C9B1]/10 hover:bg-[#00C9B1] text-[#00C9B1] hover:text-[#0E0E12] font-semibold text-xs rounded-xl transition-all flex items-center justify-center gap-2 border border-[#00C9B1]/30"
          >
            <ShoppingCart size={14} />
            + Keranjang
          </button>
        )}
      </div>
    </div>
  );
}

export default function Toko() {
  const [cart, setCart] = useState({});
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [sortBy, setSortBy] = useState('populer');
  const [showCartAlert, setShowCartAlert] = useState(false);

  const totalItems = Object.values(cart).reduce((sum, qty) => sum + qty, 0);
  const totalPrice = products.reduce((sum, p) => sum + (cart[p.id] || 0) * p.price, 0);

  const handleAddToCart = (productId, delta) => {
    setCart(prev => {
      const newQty = Math.max(0, (prev[productId] || 0) + delta);
      if (newQty === 0) {
        const updated = { ...prev };
        delete updated[productId];
        return updated;
      }
      return { ...prev, [productId]: newQty };
    });

    if (delta > 0) {
      setShowCartAlert(true);
      setTimeout(() => setShowCartAlert(false), 2000);
    }
  };

  const filtered = products
    .filter(p => {
      const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase());
      const matchCat = activeCategory === 'all' || p.category === activeCategory;
      return matchSearch && matchCat;
    })
    .sort((a, b) => {
      if (sortBy === 'harga-asc') return a.price - b.price;
      if (sortBy === 'harga-desc') return b.price - a.price;
      if (sortBy === 'rating') return b.rating - a.rating;
      return b.sold - a.sold; // populer
    });

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold dark:text-white text-[#1A202C] mb-1">Toko Perlengkapan Umroh</h2>
          <p className="text-sm dark:text-gray-400 text-gray-500">{products.length} produk pilihan untuk perjalanan ibadah Anda</p>
        </div>

        {/* Cart Summary */}
        {totalItems > 0 && (
          <div data-testid="cart-summary" className="flex-shrink-0 flex items-center gap-3 px-4 py-2.5 rounded-2xl dark:bg-[#1A1A24] bg-white dark:border dark:border-[#2D3748] border border-[#E2E8F0]">
            <div className="relative">
              <ShoppingCart size={20} className="dark:text-gray-300 text-gray-600" />
              <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-[#00C9B1] rounded-full text-[9px] font-black text-[#0E0E12] flex items-center justify-center">
                {totalItems}
              </span>
            </div>
            <div>
              <p className="text-xs dark:text-gray-400 text-gray-500">{totalItems} item</p>
              <p className="text-sm font-bold text-[#F5A623]">{formatPrice(totalPrice)}</p>
            </div>
            <button
              data-testid="checkout-btn"
              className="px-3 py-1.5 bg-[#F5A623] text-[#0E0E12] text-xs font-bold rounded-xl hover:brightness-110 transition-all"
            >
              Checkout
            </button>
          </div>
        )}
      </div>

      {/* Cart Added Alert */}
      {showCartAlert && (
        <div data-testid="cart-alert" className="mb-4 p-3 rounded-xl bg-[#00C9B1]/10 border border-[#00C9B1]/20 flex items-center gap-2 animate-fade-in">
          <CheckCircle2 size={16} className="text-[#00C9B1]" />
          <p className="text-sm text-[#00C9B1] font-medium">Produk berhasil ditambahkan ke keranjang!</p>
        </div>
      )}

      {/* Search + Sort */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 dark:text-gray-500 text-gray-400" />
          <input
            data-testid="search-products-input"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari produk perlengkapan umroh..."
            className="w-full pl-11 pr-4 py-3 rounded-xl text-sm border outline-none transition-colors focus:border-[#00C9B1] dark:bg-[#1A1A24] dark:border-[#2D3748] dark:text-white dark:placeholder-gray-600 bg-white border-[#E2E8F0] text-[#1A202C] placeholder-gray-400"
          />
        </div>
        <div className="relative">
          <SlidersHorizontal size={16} className="absolute left-3 top-1/2 -translate-y-1/2 dark:text-gray-500 text-gray-400" />
          <select
            data-testid="sort-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="pl-9 pr-8 py-3 rounded-xl text-sm border outline-none cursor-pointer dark:bg-[#1A1A24] dark:border-[#2D3748] dark:text-gray-300 bg-white border-[#E2E8F0] text-[#2D3748] focus:border-[#00C9B1] appearance-none"
          >
            <option value="populer">Terpopuler</option>
            <option value="rating">Rating Tertinggi</option>
            <option value="harga-asc">Harga Terendah</option>
            <option value="harga-desc">Harga Tertinggi</option>
          </select>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-6">
        {CATEGORIES.map(cat => (
          <button
            key={cat.id}
            data-testid={`product-category-${cat.id}`}
            onClick={() => setActiveCategory(cat.id)}
            className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all flex-shrink-0 ${
              activeCategory === cat.id
                ? 'bg-[#00C9B1] text-[#0E0E12]'
                : 'dark:bg-[#1A1A24] bg-white dark:border dark:border-[#2D3748] border border-[#E2E8F0] dark:text-gray-400 text-gray-500 dark:hover:border-[#00C9B1]/30 hover:border-[#00C9B1]/30'
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Grid */}
      {filtered.length > 0 ? (
        <div data-testid="products-grid" className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map(product => (
            <ProductCard
              key={product.id}
              product={product}
              onAddToCart={handleAddToCart}
              cartCount={cart[product.id] || 0}
            />
          ))}
        </div>
      ) : (
        <div data-testid="no-products" className="text-center py-16">
          <ShoppingCart size={40} className="mx-auto dark:text-gray-600 text-gray-300 mb-4" />
          <p className="dark:text-gray-400 text-gray-500 font-medium">Produk tidak ditemukan</p>
        </div>
      )}
    </div>
  );
}
